/**
 * PdfPassageDrawer
 *
 * In-app PDF viewer that opens a cited document at the cited passage and
 * highlights it. Clicking a reference row in the citations list opens this
 * drawer instead of a raw browser tab, mirroring TextSourceDrawer for text
 * sources.
 *
 * Passage anchoring works on the PDF.js TEXT LAYER, not on coordinates: after
 * a page's text layer renders, the cited quote (locator excerpt) is searched in
 * the concatenated, whitespace-normalized span text and the covering spans get
 * a highlight class -- the same mechanism the PDF.js find bar uses. This needs
 * no stored geometry, so it works for every already-indexed document. When
 * index-time bounding boxes exist one day (RAGFlow-style `[page, x1, x2, y1,
 * y2]` per chunk), only `applyQuoteHighlight` needs a sibling that positions
 * absolute divs over the canvas; the drawer shell stays unchanged.
 *
 * Matching is best-effort by design: exact normalized match first, then a
 * shortened prefix, across every cited page. On a miss the drawer still opens
 * at the cited page and says so ("Seite N") instead of pretending precision --
 * scanned PDFs without a text layer degrade to exactly the old page-jump.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Drawer,
  GlobalStyles,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  PictureAsPdfOutlined as PictureAsPdfOutlinedIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { diff_match_patch as DiffMatchPatch } from 'diff-match-patch';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { LocatorRegion } from '../../api/chatTypes';
import {
  buildOriginalDocumentUrl,
  buildPreviewDocumentUrl,
  viewOriginalDocument,
} from '../../utils/documents/documentHelpers';
import { log } from '../../utils/errors/logger';

/** One cited passage to locate and mark inside the PDF. */
export interface PdfPassage {
  /** 1-based page numbers the cited passage spans (first = jump target). */
  pages?: number[] | null;
  /** The cited passage to locate and highlight, if known. */
  quote?: string | null;
  /** TextQuoteSelector context (32 chars around the quote in the chunk):
   *  disambiguates repeated quotes and anchors the context match stage. */
  quotePrefix?: string | null;
  quoteSuffix?: string | null;
  /**
   * Normalized [0..1] bounding boxes of the cited text (scanned pages with
   * OCR provenance). Used as the highlight when text-layer quote anchoring
   * finds nothing — a scanned page has no text layer to anchor in.
   */
  regions?: LocatorRegion[] | null;
  /**
   * Which file to load: the document's original PDF (default) or the
   * server-side PDF preview of an Office document (converted on first request,
   * then cached — the first open can take a few seconds).
   */
  /** Citation label shown on the passage frame, e.g. "1.2". */
  label?: string | null;
  /**
   * False for context passages drawn alongside the one the user clicked
   * (every other supporting window of the document): framed lighter, never
   * the jump target. Defaults to true for the first passage.
   */
  primary?: boolean | null;
}

export interface PdfPassageTarget extends PdfPassage {
  documentId: string;
  title?: string;
  src?: 'original' | 'preview';
  /**
   * Every cited passage of the document (a document-header click). When set,
   * all of them are anchored and framed with their own labels; the first one
   * is the jump target. A row click leaves this empty and uses the inherited
   * single-passage fields.
   */
  passages?: PdfPassage[] | null;
}

/** The passages to anchor for a target: the explicit list, else the target itself. */
export function normalizePassages(target: PdfPassageTarget): PdfPassage[] {
  if (target.passages && target.passages.length > 0) {
    return target.passages;
  }
  return [
    {
      pages: target.pages,
      quote: target.quote,
      quotePrefix: target.quotePrefix,
      quoteSuffix: target.quoteSuffix,
      regions: target.regions,
      label: target.label,
      primary: target.primary,
    },
  ];
}

interface PdfPassageDrawerProps {
  target: PdfPassageTarget | null;
  onClose: () => void;
}

type MatchState = 'searching' | 'found' | 'page-only';

/**
 * Characters IGNORED during matching, on BOTH sides (quote and text layer),
 * following the PDF.js find-controller normalization model: whitespace, soft
 * hyphen, our truncation ellipsis, every hyphen/dash variant (covers
 * end-of-line hyphenation: "Dokumenten-\nverwaltung" matches the unhyphenated
 * quote once dashes and whitespace are gone), and every quote-mark variant
 * (PDFs use typographic quotes, extracted markdown often plain ones), plus
 * symbol glyphs that extraction and the text layer render differently:
 * arrows, bullets and geometric shapes (Docling emits "->" or nothing for a
 * Wingdings arrow the text layer carries as a private-use code point), the
 * private-use area itself and the object replacement character.
 */
const MATCH_SKIP_RE =
  // whitespace | soft hyphen | ellipsis | hyphen-minus | hyphen..horizontal bar
  // | minus sign | apostrophe | curly single quotes | straight double quote
  // | curly double quotes | guillemets | arrows | bullets | geometric shapes
  // | private-use area | object replacement | replacement character
  /[\s\u00AD\u2026\u002D\u2010-\u2015\u2212\u0027\u2018-\u201B\u0022\u201C-\u201F\u00AB\u00BB\u2039\u203A\u2190-\u21FF\u2022\u2023\u25AA-\u25FF\uE000-\uF8FF\uFFFC\uFFFD]/;

/** Combining marks, dropped after NFKD so "ä" (precomposed) matches "ä" (a +
 *  U+0308) — mixed forms are routine between extracted text and PDF fonts. */
const COMBINING_MARK_RE = /\p{M}/u;

/**
 * Normalization form for matching. NFKD (not NFD) additionally folds
 * compatibility forms: the "fi"/"fl" ligature glyphs (U+FB01/U+FB02) that
 * embedded fonts put in the text layer become plain letters, as do
 * full-width forms and superscript digits, so an extracted "Auftrag" matches
 * a text layer that carries "Au\uFB02rag" style ligatures.
 */
const MATCH_NORMALIZATION = 'NFKD';

/**
 * Squash the quote for matching: drop skip characters, NFKD-decompose, drop
 * combining marks, lowercase. Skip characters are tested on the ORIGINAL
 * character before decomposition (NFKD turns the ellipsis into three dots
 * that would otherwise survive). Must stay in lockstep with squashSpanText
 * (the haystack side).
 */
export function squashForMatch(text: string): string {
  let out = '';
  for (const orig of text) {
    if (MATCH_SKIP_RE.test(orig)) {
      continue;
    }
    for (const ch of orig.normalize(MATCH_NORMALIZATION)) {
      if (COMBINING_MARK_RE.test(ch) || MATCH_SKIP_RE.test(ch)) {
        continue;
      }
      out += ch.toLowerCase();
    }
  }
  return out;
}

interface SpanRange {
  /** Position of this span's squashed text within the page haystack. */
  start: number;
  end: number;
  span: HTMLElement;
  /** squashed-local char index -> char index in span.textContent. */
  map: number[];
}

/**
 * Squash one span's text like squashForMatch, but keep a char-index map back
 * to the original text so a match can be highlighted SUB-span precisely.
 * NFD may expand one original char into several squashed chars (all mapped to
 * the same original index); dropped chars simply add no map entry — so the
 * map stays exact under every length-changing normalization step.
 */
export function squashSpanText(text: string): { piece: string; map: number[] } {
  let piece = '';
  const map: number[] = [];
  for (let i = 0; i < text.length; i += 1) {
    if (MATCH_SKIP_RE.test(text[i])) {
      continue;
    }
    for (const ch of text[i].normalize(MATCH_NORMALIZATION)) {
      if (COMBINING_MARK_RE.test(ch) || MATCH_SKIP_RE.test(ch)) {
        continue;
      }
      piece += ch.toLowerCase();
      map.push(i);
    }
  }
  return { piece, map };
}

/**
 * Highlight the part of one span covered by the squashed match [from, to).
 * Wraps the exact substring in a <mark> when possible (word-precise ends);
 * falls back to marking the whole span when the DOM shape is unexpected.
 */
function markSpanSlice(range: SpanRange, from: number, to: number): void {
  const localStart = Math.max(from, range.start) - range.start;
  const localEnd = Math.min(to, range.end) - range.start;
  const node = range.span.firstChild;
  const coversWholeSpan = localStart <= 0 && localEnd >= range.end - range.start;
  if (coversWholeSpan || !(node instanceof Text) || range.span.childNodes.length !== 1) {
    range.span.classList.add('pdf-passage-highlight');
    return;
  }
  try {
    const text = node.data;
    let origStart = range.map[localStart];
    let origEnd = Math.min(range.map[localEnd - 1] + 1, node.length);
    // Visually complete the mark: adjacent NON-whitespace skip characters
    // (hyphens, quote marks) are not part of the matched content but belong
    // to the highlighted words — "Fuer An-" instead of "Fuer An".
    while (
      origEnd < text.length &&
      MATCH_SKIP_RE.test(text[origEnd]) &&
      !/\s/.test(text[origEnd])
    ) {
      origEnd += 1;
    }
    while (
      origStart > 0 &&
      MATCH_SKIP_RE.test(text[origStart - 1]) &&
      !/\s/.test(text[origStart - 1])
    ) {
      origStart -= 1;
    }
    const domRange = document.createRange();
    domRange.setStart(node, origStart);
    domRange.setEnd(node, origEnd);
    const mark = document.createElement('mark');
    mark.className = 'pdf-passage-highlight';
    domRange.surroundContents(mark);
  } catch {
    range.span.classList.add('pdf-passage-highlight');
  }
}

/**
 * Adaptive highlight colour.
 *
 * A fixed amber highlight clashes with documents that themselves use yellow
 * or orange (SAP warning icons, screenshot call-outs). The page canvas is
 * sampled once per page: saturated pixels are binned by hue and the highlight
 * takes the candidate hue farthest from every dominant hue on the page. Pages
 * without saturated content (plain black-on-white text) keep the amber
 * default so highlights look the same across ordinary documents.
 */
export const DEFAULT_HIGHLIGHT_HUE = 45;
const HIGHLIGHT_HUE_CANDIDATES = [DEFAULT_HIGHLIGHT_HUE, 205, 145, 300, 270, 20];
const HUE_BINS = 12;

function circularHueDistance(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

/**
 * Pick the highlight hue for a page from its RGBA pixel data, or null when
 * the page has no dominant saturated colour (use the default).
 */
export function pickHighlightHue(data: ArrayLike<number>, pixelCount: number): number | null {
  const bins = new Array<number>(HUE_BINS).fill(0);
  let saturated = 0;
  for (let i = 0; i < pixelCount; i += 1) {
    const r = data[i * 4] / 255;
    const g = data[i * 4 + 1] / 255;
    const b = data[i * 4 + 2] / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;
    const sat = max === 0 ? 0 : delta / max;
    if (sat < 0.25 || max < 0.2) {
      continue; // white paper, black text, greys
    }
    let hue: number;
    if (max === r) {
      hue = ((g - b) / delta) % 6;
    } else if (max === g) {
      hue = (b - r) / delta + 2;
    } else {
      hue = (r - g) / delta + 4;
    }
    hue = (hue * 60 + 360) % 360;
    bins[Math.floor(hue / (360 / HUE_BINS)) % HUE_BINS] += 1;
    saturated += 1;
  }
  if (saturated < pixelCount * 0.02) {
    return null;
  }
  const dominant: number[] = [];
  bins.forEach((count, index) => {
    if (count >= saturated * 0.05) {
      dominant.push(index * (360 / HUE_BINS) + 360 / HUE_BINS / 2);
    }
  });
  let best = DEFAULT_HIGHLIGHT_HUE;
  let bestScore = -1;
  for (const candidate of HIGHLIGHT_HUE_CANDIDATES) {
    const score = Math.min(...dominant.map((h) => circularHueDistance(candidate, h)));
    if (score > bestScore) {
      bestScore = score;
      best = candidate;
    }
  }
  return best;
}

/** CSS colours for a highlight hue (fill for text marks, outline for regions). */
export function highlightColorsForHue(hue: number): { fill: string; outline: string } {
  if (hue === DEFAULT_HIGHLIGHT_HUE) {
    return { fill: 'rgba(255, 213, 79, 0.55)', outline: 'rgba(255, 179, 0, 0.9)' };
  }
  return { fill: `hsla(${hue}, 90%, 55%, 0.4)`, outline: `hsla(${hue}, 90%, 42%, 0.9)` };
}

/**
 * Sample the rendered page and store the chosen colours as CSS variables on
 * the page wrapper; the highlight rules read them with the amber fallback.
 */
function applyAdaptiveHighlight(wrapper: HTMLElement, canvas: HTMLCanvasElement): void {
  try {
    const size = 48;
    const probe = document.createElement('canvas');
    probe.width = size;
    probe.height = size;
    const ctx = probe.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      return;
    }
    ctx.drawImage(canvas, 0, 0, size, size);
    const { data } = ctx.getImageData(0, 0, size, size);
    const hue = pickHighlightHue(data, size * size) ?? DEFAULT_HIGHLIGHT_HUE;
    const colors = highlightColorsForHue(hue);
    wrapper.style.setProperty('--passage-highlight', colors.fill);
    wrapper.style.setProperty('--passage-outline', colors.outline);
  } catch {
    // Tainted canvas or unsupported context: keep the default colours.
  }
}

/**
 * Draw one frame per page and passage around everything that was marked for
 * it (text marks or region overlays) and pin a citation label to its corner.
 * Marks carry ``data-passage`` (set by applyQuoteHighlight's ``tag``) so
 * several passages on one page get their own frame and label; untagged marks
 * form one group. ``label`` is a string or a function of the passage tag.
 * The frame uses the app colour, not the adaptive fill, so the cited spot
 * stays recognisable even when the page itself uses the highlight colour; a
 * short pulse on arrival draws the eye to it.
 */
export function drawPassageFrames(
  wrappers: HTMLElement[],
  label: string | ((tag: string) => string),
  isPrimary: (tag: string) => boolean = () => true
): number {
  let drawn = 0;
  for (const wrapper of wrappers) {
    for (const old of Array.from(wrapper.querySelectorAll('.pdf-passage-frame'))) {
      old.remove();
    }
    const marks = Array.from(
      wrapper.querySelectorAll<HTMLElement>('.pdf-passage-highlight, .pdf-passage-region')
    );
    if (marks.length === 0) {
      continue;
    }
    const groups = new Map<string, HTMLElement[]>();
    for (const mark of marks) {
      const tag = mark.dataset.passage ?? '';
      const list = groups.get(tag);
      if (list) {
        list.push(mark);
      } else {
        groups.set(tag, [mark]);
      }
    }
    const base = wrapper.getBoundingClientRect();
    for (const [tag, groupMarks] of groups) {
      let left = Infinity;
      let top = Infinity;
      let right = -Infinity;
      let bottom = -Infinity;
      for (const mark of groupMarks) {
        const r = mark.getBoundingClientRect();
        if (r.width === 0 && r.height === 0) {
          continue;
        }
        left = Math.min(left, r.left - base.left);
        top = Math.min(top, r.top - base.top);
        right = Math.max(right, r.right - base.left);
        bottom = Math.max(bottom, r.bottom - base.top);
      }
      const frame = document.createElement('div');
      frame.className = isPrimary(tag) ? 'pdf-passage-frame' : 'pdf-passage-frame pdf-passage-frame--secondary';
      frame.dataset.passage = tag;
      if (Number.isFinite(left)) {
        const pad = 6;
        frame.style.left = `${Math.max(0, left - pad)}px`;
        frame.style.top = `${Math.max(0, top - pad)}px`;
        frame.style.width = `${right - left + pad * 2}px`;
        frame.style.height = `${bottom - top + pad * 2}px`;
      }
      const tagEl = document.createElement('span');
      tagEl.className = 'pdf-passage-frame-label';
      tagEl.textContent = typeof label === 'function' ? label(tag) : label;
      frame.appendChild(tagEl);
      wrapper.appendChild(frame);
      drawn += 1;
    }
  }
  return drawn;
}

/** Optional TextQuoteSelector-style context around the quote. */
export interface QuoteContext {
  prefix?: string | null;
  suffix?: string | null;
  /** Scroll the first mark into view (default true; false for secondary passages). */
  scroll?: boolean;
  /** Passage tag stamped onto the new marks as ``data-passage`` (per-passage frames). */
  tag?: string;
}

/** How a quote was anchored, for the miss-rate instrumentation. */
export type QuoteMatchMethod = 'exact' | 'context' | 'fuzzy' | 'segments';

/**
 * Search the quote in a rendered text layer and highlight it.
 * Returns the anchoring method used, or null when nothing was highlighted.
 *
 * Matching is WHITESPACE-FREE on both sides: PDF text items split words at
 * arbitrary points ("Entwicklun" + "g User-Exit"), so any joiner between span
 * texts corrupts words and any whitespace difference breaks the match. With
 * all whitespace removed from haystack and needle, split words, line breaks
 * and spacing variations all become irrelevant.
 *
 * Anchoring is a fallback ladder (Hypothesis / PDF.js-findbar model):
 * 1. context+quote exact (prefix disambiguates repeated quotes),
 * 2. quote exact, then progressively shorter prefixes — a partial hit is
 *    EXTENDED to the longest matching run of the full quote and the cut-off
 *    trailing word is completed,
 * 3. Bitap fuzzy (diff-match-patch) on the quote head, tolerating
 *    character-level drift between stored excerpt and rendered text layer.
 */
export function applyQuoteHighlight(
  textLayer: HTMLElement | HTMLElement[],
  quote: string,
  context?: QuoteContext
): QuoteMatchMethod | null {
  // Several layers (the cited pages, in page order) form ONE haystack so a
  // quote that runs across a page break is matched and marked as a whole.
  const layers = Array.isArray(textLayer) ? textLayer : [textLayer];
  const method = anchorQuote(layers, quote, context);
  if (method && context?.tag !== undefined) {
    for (const layer of layers) {
      for (const mark of Array.from(
        layer.querySelectorAll<HTMLElement>('.pdf-passage-highlight:not([data-passage])')
      )) {
        mark.dataset.passage = context.tag;
      }
    }
  }
  return method;
}

function anchorQuote(
  layers: HTMLElement[],
  quote: string,
  context?: QuoteContext
): QuoteMatchMethod | null {
  const doScroll = context?.scroll !== false;
  const spans = layers
    .flatMap((layer) => Array.from(layer.querySelectorAll<HTMLElement>('span')))
    .filter((s) => (s.textContent ?? '') !== '');
  if (spans.length === 0) {
    return null;
  }

  let haystack = '';
  const ranges: SpanRange[] = [];
  for (const span of spans) {
    const { piece, map } = squashSpanText(span.textContent ?? '');
    if (!piece) {
      continue;
    }
    const start = haystack.length;
    haystack += piece;
    ranges.push({ start, end: haystack.length, span, map });
  }

  const fullNeedle = squashForMatch(quote);
  if (fullNeedle.length < 12) {
    return null;
  }

  const markMatch = (at: number, matchEnd: number): boolean => {
    let firstSpan: HTMLElement | null = null;
    for (const r of ranges) {
      if (r.end > at && r.start < matchEnd) {
        markSpanSlice(r, at, matchEnd);
        firstSpan ??= r.span;
      }
    }
    if (firstSpan) {
      if (doScroll) {
        firstSpan.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      return true;
    }
    return false;
  };

  // Candidate needles, most specific first. `skip` chars are matched for
  // disambiguation but not highlighted (the context prefix).
  const candidates: Array<{ needle: string; skip: number; method: QuoteMatchMethod }> = [];
  const prefixNeedle = context?.prefix ? squashForMatch(context.prefix) : '';
  if (prefixNeedle.length >= 8) {
    candidates.push({
      needle: prefixNeedle + fullNeedle,
      skip: prefixNeedle.length,
      method: 'context',
    });
  }
  candidates.push({ needle: fullNeedle, skip: 0, method: 'exact' });
  for (const len of [160, 80, 40]) {
    if (fullNeedle.length > len) {
      candidates.push({ needle: fullNeedle.slice(0, len), skip: 0, method: 'exact' });
    }
  }

  // TextQuoteSelector suffix as the tie-breaker for repeated quotes: when the
  // full quote occurs more than once and no prefix pinned it, the occurrence
  // whose following text agrees longest with the stored suffix wins.
  const suffixNeedle = context?.suffix ? squashForMatch(context.suffix) : '';
  const pickOccurrence = (needle: string): number => {
    const first = haystack.indexOf(needle);
    if (first < 0 || suffixNeedle.length < 8) {
      return first;
    }
    let best = first;
    let bestAgreement = -1;
    let at = first;
    let guard = 20;
    while (at >= 0 && guard > 0) {
      let agreement = 0;
      const tail = at + needle.length;
      while (
        agreement < suffixNeedle.length &&
        tail + agreement < haystack.length &&
        haystack[tail + agreement] === suffixNeedle[agreement]
      ) {
        agreement += 1;
      }
      if (agreement > bestAgreement) {
        bestAgreement = agreement;
        best = at;
      }
      at = haystack.indexOf(needle, at + 1);
      guard -= 1;
    }
    return best;
  };

  for (const { needle, skip, method } of candidates) {
    const found = skip === 0 ? pickOccurrence(needle) : haystack.indexOf(needle);
    if (found < 0) {
      continue;
    }
    const at = found + skip;
    // Extend a partial hit to the longest run of the full quote that matches.
    let len = needle.length - skip;
    while (
      at + len < haystack.length &&
      len < fullNeedle.length &&
      haystack[at + len] === fullNeedle[len]
    ) {
      len += 1;
    }
    let matchEnd = at + len;
    // A partial match ends at an arbitrary character; complete the trailing
    // word (bounded) so the highlight never stops mid-word. A full-quote
    // match already ends on a word boundary -- do not extend it, the squashed
    // haystack has no separators and the next word would bleed in.
    if (len < fullNeedle.length) {
      let guard = 24;
      while (
        matchEnd < haystack.length &&
        guard > 0 &&
        /[\p{L}\p{N}]/u.test(haystack[matchEnd])
      ) {
        matchEnd += 1;
        guard -= 1;
      }
    }
    if (markMatch(at, matchEnd)) {
      return method;
    }
  }

  // Fuzzy fallback (Bitap via diff-match-patch): anchors the quote HEAD
  // within an edit-distance threshold anywhere on the page — tolerates
  // character-level drift (OCR noise, exotic encodings) that defeats every
  // exact stage. Pattern capped at 32 chars (Bitap word size).
  const dmp = new DiffMatchPatch();
  dmp.Match_Threshold = 0.4;
  dmp.Match_Distance = haystack.length * 2;
  const pattern = fullNeedle.slice(0, 32);
  const fuzzyAt = dmp.match_main(haystack, pattern, 0);
  if (fuzzyAt >= 0) {
    let fuzzyEnd = Math.min(fuzzyAt + fullNeedle.length, haystack.length);
    let verified = true;
    // Verify-before-accept (Hypothesis ladder principle): a head that merely
    // RESEMBLES other text (e.g. a repeated document title) must not anchor
    // the whole quote there. For long quotes, require the quote TAIL to also
    // fuzzy-match near the expected end position; otherwise fall through to
    // the segment stage below instead of highlighting the wrong place.
    if (fullNeedle.length >= 64) {
      const tailPattern = fullNeedle.slice(-32);
      const expectedTailAt = Math.min(
        fuzzyAt + fullNeedle.length - 32,
        Math.max(haystack.length - 32, 0)
      );
      dmp.Match_Distance = 200;
      const tailAt = dmp.match_main(haystack, tailPattern, expectedTailAt);
      if (tailAt < 0 || Math.abs(tailAt - expectedTailAt) > 100) {
        verified = false;
      } else {
        fuzzyEnd = Math.min(tailAt + 32, haystack.length);
      }
    }
    if (verified && markMatch(fuzzyAt, fuzzyEnd)) {
      return 'fuzzy';
    }
  }

  // Segment fallback: the quote as a whole is not on the page, but most of it
  // is, in pieces. Typical causes are tables (Docling reads cells in a
  // different order than the text layer) and Docling heading prefixes glued
  // in front of the body. Greedily find the exact runs of the quote that
  // exist on the page (at least MIN_RUN squashed chars each), keep the runs
  // that are not dwarfed by the longest one, and accept only when they cover
  // at least half of the quote. The last two guards keep a short head that
  // merely resembles the document title from anchoring on its own.
  const segments = findQuoteSegments(haystack, fullNeedle);
  if (segments.length > 0) {
    let first: HTMLElement | null = null;
    for (const seg of segments) {
      for (const r of ranges) {
        if (r.end > seg.at && r.start < seg.at + seg.len) {
          markSpanSlice(r, seg.at, seg.at + seg.len);
          first ??= r.span;
        }
      }
    }
    if (first) {
      if (doScroll) {
        first.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      return 'segments';
    }
  }
  return null;
}

const SEGMENT_MIN_RUN = 24;
const SEGMENT_MAX_OCCURRENCES = 20;

/**
 * Exact runs of `needle` that occur in `haystack`, greedy from the start.
 * Returns the runs (sorted by haystack position) when they cover at least
 * half of the needle; otherwise an empty list. Exported for tests.
 */
export function findQuoteSegments(
  haystack: string,
  needle: string
): Array<{ at: number; len: number }> {
  const runs: Array<{ at: number; len: number }> = [];
  let i = 0;
  while (i + SEGMENT_MIN_RUN <= needle.length) {
    const head = needle.slice(i, i + SEGMENT_MIN_RUN);
    // Among all occurrences of the head, take the one that extends furthest.
    let bestAt = -1;
    let bestLen = 0;
    let from = 0;
    for (let n = 0; n < SEGMENT_MAX_OCCURRENCES; n += 1) {
      const at = haystack.indexOf(head, from);
      if (at < 0) {
        break;
      }
      let len = SEGMENT_MIN_RUN;
      while (
        i + len < needle.length &&
        at + len < haystack.length &&
        haystack[at + len] === needle[i + len]
      ) {
        len += 1;
      }
      if (len > bestLen) {
        bestLen = len;
        bestAt = at;
      }
      from = at + 1;
    }
    if (bestAt < 0) {
      i += 1;
      continue;
    }
    runs.push({ at: bestAt, len: bestLen });
    i += bestLen;
  }
  if (runs.length === 0) {
    return [];
  }
  const longest = Math.max(...runs.map((r) => r.len));
  const kept = runs.filter((r) => r.len >= longest * 0.4);
  const covered = kept.reduce((sum, r) => sum + r.len, 0);
  if (covered < needle.length * 0.5) {
    return [];
  }
  return kept.sort((a, b) => a.at - b.at);
}

export const PdfPassageDrawer: React.FC<PdfPassageDrawerProps> = ({ target, onClose }) => {
  const { t } = useTranslation();
  const open = target !== null;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [matchState, setMatchState] = useState<MatchState>('searching');
  // Found / total passages, for the header chip when several are marked.
  const [matchCount, setMatchCount] = useState<{ found: number; total: number }>({ found: 0, total: 1 });
  // Visible page for the header counter; total is known once the PDF loads.
  const [pageInfo, setPageInfo] = useState<{ current: number; total: number } | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pagesRef = useRef<HTMLDivElement | null>(null);

  const targetPage = target?.pages?.[0] ?? 1;

  const openExternally = useCallback(() => {
    if (target) {
      void viewOriginalDocument(target.documentId, `#page=${targetPage}`);
    }
  }, [target, targetPage]);

  useEffect(() => {
    if (!target) {
      return undefined;
    }
    let cancelled = false;
    let cleanupScroll: (() => void) | null = null;
    let loadingTask: { destroy: () => Promise<void> } | null = null;
    setLoading(true);
    setError(null);
    setMatchState('searching');
    setMatchCount({ found: 0, total: 1 });
    setPageInfo(null);

    const run = async () => {
      const pdfjs = await import('pdfjs-dist');
      pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
      const task = pdfjs.getDocument({
        url:
          target.src === 'preview'
            ? buildPreviewDocumentUrl(target.documentId)
            : buildOriginalDocumentUrl(target.documentId),
        withCredentials: true,
      });
      loadingTask = task;
      const doc = await task.promise;
      if (cancelled) {
        return;
      }

      const host = pagesRef.current;
      const scroller = scrollRef.current;
      if (!host || !scroller) {
        return;
      }
      host.replaceChildren();

      // Width-fit scale from page 1 (per-page viewports are still measured
      // individually below, so mixed page sizes render correctly).
      const firstPage = await doc.getPage(1);
      const baseViewport = firstPage.getViewport({ scale: 1 });
      const scale = Math.max(0.5, (scroller.clientWidth - 32) / baseViewport.width);

      // One anchoring job per cited passage. A row click gives one passage;
      // a document-header click gives every cited passage of the document,
      // each with its own label. The first passage is the jump target.
      interface PassageJob {
        tag: string;
        label: string | null;
        quote: string | null;
        prefix: string | null;
        suffix: string | null;
        citedPages: Set<number>;
        regionsByPage: Map<number, LocatorRegion[]>;
        found: boolean;
        primary: boolean;
      }
      const jobs: PassageJob[] = normalizePassages(target).map((passage, idx) => {
        const citedPages = new Set(
          (passage.pages ?? []).filter((p) => Number.isFinite(p) && p >= 1 && p <= doc.numPages)
        );
        // OCR bbox provenance (scanned pages): group by page and treat region
        // pages as cited so they render eagerly and are jumpable.
        const regionsByPage = new Map<number, LocatorRegion[]>();
        for (const reg of passage.regions ?? []) {
          const page = Math.floor(reg.page);
          if (page < 1 || page > doc.numPages) {
            continue;
          }
          const list = regionsByPage.get(page);
          if (list) {
            list.push(reg);
          } else {
            regionsByPage.set(page, [reg]);
          }
          citedPages.add(page);
        }
        return {
          tag: String(idx),
          label: passage.label ?? null,
          quote: passage.quote ?? null,
          prefix: passage.quotePrefix ?? null,
          suffix: passage.quoteSuffix ?? null,
          citedPages,
          regionsByPage,
          found: false,
          primary: passage.primary ?? idx === 0,
        };
      });
      setMatchCount({ found: 0, total: jobs.length });
      const allCitedPages = new Set<number>();
      for (const job of jobs) {
        for (const p of job.citedPages) {
          allCitedPages.add(p);
        }
      }
      // No cited pages but a quote (e.g. a DOCX preview whose chunks carry
      // section headings, not page numbers): scan pages from the start until
      // the passage is found, bounded to keep worst-case work sane.
      const scansAllPages = (job: PassageJob) => Boolean(job.quote) && job.citedPages.size === 0;
      const searchAllPages = jobs.some(scansAllPages);
      const searchPageLimit = Math.min(doc.numPages, 25);
      const primary = jobs[0];
      const firstRegionPage = Math.min(...primary.regionsByPage.keys());
      const jumpPage = Math.min(
        Math.max(
          [...primary.citedPages].sort((a, b) => a - b)[0] ??
            (primary.regionsByPage.size > 0 ? firstRegionPage : targetPage),
          1
        ),
        doc.numPages
      );

      const wrappers: HTMLDivElement[] = [];
      const rendered = new Set<number>();
      // One render promise per page: the IntersectionObserver and the eager
      // cited-page loop may both ask for the same page, and the loop must
      // wait for the render (and its quote search) that is already running,
      // otherwise the page-only verdict below fires before the match lands.
      const renders = new Map<number, Promise<void>>();
      const textLayers = new Map<number, HTMLElement>();
      let foundCount = 0;
      const labelFor = (tag: string): string => {
        const job = jobs[Number(tag)];
        return job?.label
          ? t('chat.sources.pdfViewer.frameLabel', { defaultValue: 'Quelle {{label}}', label: job.label })
          : t('chat.sources.pdfViewer.frameLabelPlain', { defaultValue: 'Zitierte Stelle' });
      };
      const drawFrames = () => {
        drawPassageFrames(
          wrappers.filter((w) => w.querySelector('.pdf-passage-highlight, .pdf-passage-region')),
          labelFor,
          (tag) => jobs[Number(tag)]?.primary !== false
        );
      };
      // Miss-rate instrumentation (Stufe-3 decision basis): every passage
      // logs found vs page_only, so the fraction of quote-anchoring misses
      // is measurable before investing in index-time bboxes.
      const markFound = (job: PassageJob, outcome: string, extra: Record<string, unknown>) => {
        job.found = true;
        foundCount += 1;
        setMatchState('found');
        setMatchCount({ found: foundCount, total: jobs.length });
        drawFrames();
        log.user.action('pdf_passage_match', 'PdfPassageDrawer', {
          outcome,
          src: target.src ?? 'original',
          had_cited_pages: job.citedPages.size > 0,
          document_id: target.documentId,
          passage: job.label ?? null,
          ...extra,
        });
      };

      const renderPage = (pageNo: number): Promise<void> => {
        const pending = renders.get(pageNo);
        if (pending) {
          return pending;
        }
        const job = renderPageOnce(pageNo);
        renders.set(pageNo, job);
        return job;
      };

      const renderPageOnce = async (pageNo: number) => {
        if (cancelled || rendered.has(pageNo)) {
          return;
        }
        rendered.add(pageNo);
        const page = await doc.getPage(pageNo);
        if (cancelled) {
          return;
        }
        const viewport = page.getViewport({ scale });
        const wrapper = wrappers[pageNo - 1];
        wrapper.style.width = `${viewport.width}px`;
        wrapper.style.height = `${viewport.height}px`;
        wrapper.style.setProperty('--scale-factor', String(viewport.scale));

        const canvas = document.createElement('canvas');
        const outputScale = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        wrapper.replaceChildren(canvas);
        await page.render({
          canvas,
          viewport,
          transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined,
        }).promise;

        applyAdaptiveHighlight(wrapper, canvas);

        const textLayerDiv = document.createElement('div');
        textLayerDiv.className = 'textLayer pdf-passage-textlayer';
        textLayers.set(pageNo, textLayerDiv);
        wrapper.appendChild(textLayerDiv);
        const textLayer = new pdfjs.TextLayer({
          textContentSource: page.streamTextContent(),
          container: textLayerDiv,
          viewport,
        });
        await textLayer.render();

        // Quote anchoring on cited pages (or on every page in scan mode).
        // A passage with a single cited page (or in scan mode) is anchored
        // right after rendering. Passages spanning several cited pages are
        // searched as one haystack further below, so a quote crossing a page
        // break is marked on both pages. Only the first hit scrolls; further
        // passages are marked in place.
        for (const job of jobs) {
          if (cancelled || job.found || !job.quote) {
            continue;
          }
          if (!(scansAllPages(job) || (job.citedPages.has(pageNo) && job.citedPages.size === 1))) {
            continue;
          }
          const method = applyQuoteHighlight(textLayerDiv, job.quote, {
            prefix: job.prefix,
            suffix: job.suffix,
            tag: job.tag,
            scroll: foundCount === 0,
          });
          if (method) {
            // applyQuoteHighlight already scrolled the first mark to the
            // viewport centre. Do NOT jump to the page top here: that instant
            // scroll cancelled the smooth one and left passages in the lower
            // half of a page below the fold.
            markFound(job, 'found', { match_method: method });
          }
        }
      };

      // Placeholders for every page (estimated at page-1 size until rendered),
      // lazily rendered as they scroll near the viewport.
      const estHeight = baseViewport.height * scale;
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              const pageNo = Number((entry.target as HTMLElement).dataset.page);
              void renderPage(pageNo);
            }
          }
        },
        { root: scroller, rootMargin: '600px 0px' }
      );
      for (let i = 1; i <= doc.numPages; i += 1) {
        const wrapper = document.createElement('div');
        wrapper.className = 'pdf-passage-page';
        wrapper.dataset.page = String(i);
        wrapper.style.width = `${baseViewport.width * scale}px`;
        wrapper.style.height = `${estHeight}px`;
        host.appendChild(wrapper);
        wrappers.push(wrapper);
        observer.observe(wrapper);
      }

      setPageInfo({ current: jumpPage, total: doc.numPages });
      // Header page counter: the page with the largest visible area.
      const updateCurrentPage = () => {
        const viewTop = scroller.scrollTop;
        const viewBottom = viewTop + scroller.clientHeight;
        let current = 1;
        let bestVisible = -1;
        for (let i = 0; i < wrappers.length; i += 1) {
          const top = wrappers[i].offsetTop;
          const bottom = top + wrappers[i].offsetHeight;
          const visible = Math.min(bottom, viewBottom) - Math.max(top, viewTop);
          if (visible > bestVisible) {
            bestVisible = visible;
            current = i + 1;
          }
        }
        setPageInfo({ current, total: doc.numPages });
      };
      let scrollRaf = 0;
      const onScroll = () => {
        if (scrollRaf) {
          return;
        }
        scrollRaf = window.requestAnimationFrame(() => {
          scrollRaf = 0;
          updateCurrentPage();
        });
      };
      scroller.addEventListener('scroll', onScroll, { passive: true });
      cleanupScroll = () => {
        scroller.removeEventListener('scroll', onScroll);
        if (scrollRaf) {
          window.cancelAnimationFrame(scrollRaf);
        }
      };

      setLoading(false);
      // Jump to the cited page, render it and every other cited page eagerly,
      // then let the quote search refine the scroll to the exact passage.
      wrappers[jumpPage - 1]?.scrollIntoView({ block: 'start' });
      await renderPage(jumpPage);
      for (const p of [...allCitedPages].sort((a, b) => a - b)) {
        if (p !== jumpPage) {
          await renderPage(p);
        }
      }
      if (searchAllPages) {
        for (
          let p = 1;
          p <= searchPageLimit && jobs.some((j) => scansAllPages(j) && !j.found) && !cancelled;
          p += 1
        ) {
          await renderPage(p);
        }
      }
      // Multi-page passages: one search over their cited pages in order.
      for (const job of jobs) {
        if (cancelled || job.found || !job.quote || job.citedPages.size <= 1) {
          continue;
        }
        const layers = [...job.citedPages]
          .sort((a, b) => a - b)
          .map((p) => textLayers.get(p))
          .filter((layer): layer is HTMLElement => Boolean(layer));
        const method = layers.length > 0
          ? applyQuoteHighlight(layers, job.quote, {
              prefix: job.prefix,
              suffix: job.suffix,
              tag: job.tag,
              scroll: foundCount === 0,
            })
          : null;
        if (method) {
          markFound(job, 'found', { match_method: method, multi_page: true });
        }
      }
      // Region fallback: when quote anchoring found nothing (scanned pages
      // have no text layer to anchor in) but OCR bbox provenance exists, draw
      // percent-positioned overlays on the cited regions instead.
      for (const job of jobs) {
        if (cancelled || job.found || job.regionsByPage.size === 0) {
          continue;
        }
        let firstOverlay: HTMLElement | null = null;
        for (const [pageNo, regs] of job.regionsByPage) {
          const wrapper = wrappers[pageNo - 1];
          if (!wrapper || !rendered.has(pageNo)) {
            continue;
          }
          for (const reg of regs) {
            const overlay = document.createElement('div');
            overlay.className = 'pdf-passage-region';
            overlay.dataset.passage = job.tag;
            overlay.style.left = `${reg.x * 100}%`;
            overlay.style.top = `${reg.y * 100}%`;
            overlay.style.width = `${reg.w * 100}%`;
            overlay.style.height = `${reg.h * 100}%`;
            wrapper.appendChild(overlay);
            firstOverlay ??= overlay;
          }
        }
        if (firstOverlay) {
          const scroll = foundCount === 0;
          markFound(job, 'region', {});
          if (scroll) {
            firstOverlay.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        }
      }
      if (!cancelled && foundCount === 0) {
        setMatchState('page-only');
        log.user.action('pdf_passage_match', 'PdfPassageDrawer', {
          outcome: 'page_only',
          src: target.src ?? 'original',
          had_cited_pages: allCitedPages.size > 0,
          had_quote: jobs.some((j) => Boolean(j.quote)),
          passages: jobs.length,
          document_id: target.documentId,
        });
      } else if (!cancelled && foundCount < jobs.length) {
        log.user.action('pdf_passage_match', 'PdfPassageDrawer', {
          outcome: 'partial',
          found: foundCount,
          passages: jobs.length,
          src: target.src ?? 'original',
          document_id: target.documentId,
        });
      }
    };

    run().catch((e: unknown) => {
      if (!cancelled) {
        log.error('PdfPassageDrawer: failed to load PDF', { error: String(e) });
        setError(t('chat.sources.pdfViewer.loadError', {
          defaultValue: 'Das PDF konnte nicht geladen werden.',
        }));
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      cleanupScroll?.();
      // Destroying the loading task aborts network requests and tears down the
      // worker + document in one call.
      if (loadingTask) {
        void loadingTask.destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run only per target
  }, [target]);

  const statusChip = () => {
    if (loading || matchState === 'searching') {
      return null;
    }
    if (matchState === 'found') {
      return (
        <Chip
          size="small"
          color="primary"
          variant="outlined"
          label={
            matchCount.total > 1
              ? t('chat.sources.pdfViewer.passagesFound', {
                  defaultValue: '{{found}} von {{total}} Stellen markiert',
                  found: matchCount.found,
                  total: matchCount.total,
                })
              : t('chat.sources.pdfViewer.passageFound', { defaultValue: 'Passage markiert' })
          }
        />
      );
    }
    return (
      <Chip
        size="small"
        variant="outlined"
        label={t('chat.sources.pdfViewer.pageOnly', {
          defaultValue: 'Seite {{page}}',
          page: targetPage,
        })}
      />
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // The layout AppBar is fixed at zIndex.drawer + 1; a viewer must sit
      // above it, otherwise its header row hides behind the bar.
      sx={{ zIndex: (theme) => theme.zIndex.modal }}
      slotProps={{ paper: { sx: { width: { xs: '100vw', md: 'min(920px, 92vw)' } } } }}
    >
      <GlobalStyles
        styles={{
          '.pdf-passage-page': {
            position: 'relative',
            margin: '0 auto 16px auto',
            boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
            backgroundColor: '#ffffff',
          },
          // Mirrors pdf.js' official text_layer.css: the TextLayer stores its
          // measured width correction in the per-span custom properties
          // --font-height / --scale-x, and only these rules APPLY them.
          // Without the font-size calc + scaleX transform the transparent
          // text renders at the page's inherited font metrics and drifts
          // horizontally against the canvas — highlights then sit a few
          // words left of the glyphs they mark (2026-09-02 regression).
          '.pdf-passage-textlayer': {
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            lineHeight: 1,
            letterSpacing: 'normal',
            wordSpacing: 'normal',
            textSizeAdjust: 'none',
            forcedColorAdjust: 'none',
            transformOrigin: '0 0',
            '--total-scale-factor': 'calc(var(--scale-factor, 1) * var(--user-unit, 1))',
          },
          '.pdf-passage-textlayer span, .pdf-passage-textlayer br': {
            color: 'transparent',
            position: 'absolute',
            whiteSpace: 'pre',
            cursor: 'text',
            transformOrigin: '0% 0%',
            fontSize: 'calc(var(--total-scale-factor) * var(--font-height, 0))',
            transform: 'rotate(var(--rotate, 0deg)) scaleX(var(--scale-x, 1))',
          },
          // Colours come from the per-page CSS variables set by
          // applyAdaptiveHighlight; the fallbacks are the amber default.
          '.pdf-passage-textlayer .pdf-passage-highlight': {
            backgroundColor: 'var(--passage-highlight, rgba(255, 213, 79, 0.55))',
            mixBlendMode: 'multiply',
            borderRadius: '2px',
            color: 'transparent',
          },
          '@keyframes pdf-passage-pulse': {
            '0%': { boxShadow: '0 0 0 0 rgba(0, 48, 100, 0.55)' },
            '70%': { boxShadow: '0 0 0 14px rgba(0, 48, 100, 0)' },
            '100%': { boxShadow: '0 0 0 0 rgba(0, 48, 100, 0)' },
          },
          // Frame around the marked passage in the app colour (independent of
          // the adaptive fill) with the citation label pinned to its corner.
          '.pdf-passage-frame': {
            position: 'absolute',
            border: '2px solid #003064',
            borderRadius: '6px',
            pointerEvents: 'none',
            animation: 'pdf-passage-pulse 1.4s ease-out 2',
          },
          // Other supporting windows of the same document: visible but
          // clearly subordinate to the clicked passage.
          '.pdf-passage-frame--secondary': {
            borderStyle: 'dashed',
            borderColor: 'rgba(0, 48, 100, 0.45)',
            animation: 'none',
          },
          '.pdf-passage-frame--secondary .pdf-passage-frame-label': {
            backgroundColor: 'rgba(0, 48, 100, 0.55)',
          },
          '.pdf-passage-frame-label': {
            position: 'absolute',
            top: '-11px',
            left: '8px',
            padding: '1px 8px',
            borderRadius: '10px',
            backgroundColor: '#003064',
            color: '#ffffff',
            fontFamily: '"Roboto", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
            fontSize: '0.7rem',
            fontWeight: 600,
            lineHeight: '18px',
            whiteSpace: 'nowrap',
            boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
          },
          '.pdf-passage-region': {
            position: 'absolute',
            backgroundColor: 'var(--passage-highlight, rgba(255, 213, 79, 0.35))',
            mixBlendMode: 'multiply',
            outline: '2px solid var(--passage-outline, rgba(255, 179, 0, 0.9))',
            borderRadius: '3px',
            pointerEvents: 'none',
          },
        }}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: '1px solid #dde3ea',
        }}
      >
        <PictureAsPdfOutlinedIcon sx={{ color: '#b3261e' }} />
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '0.95rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: '1 1 auto',
          }}
        >
          {target?.title ?? ''}
        </Typography>
        {pageInfo && (
          <Typography
            sx={{ fontSize: '0.8rem', color: '#5e6b7a', whiteSpace: 'nowrap', flexShrink: 0 }}
            aria-live="polite"
          >
            {t('chat.sources.pdfViewer.pageCounter', {
              defaultValue: 'Seite {{current}} / {{total}}',
              current: pageInfo.current,
              total: pageInfo.total,
            })}
          </Typography>
        )}
        {statusChip()}
        <Tooltip title={t('chat.sources.openDocument', { defaultValue: 'Open document' })}>
          <IconButton size="small" onClick={openExternally} aria-label="open in new tab">
            <OpenInNewIcon sx={{ fontSize: '1.1rem' }} />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon sx={{ fontSize: '1.2rem' }} />
        </IconButton>
      </Box>
      <Box ref={scrollRef} sx={{ flex: '1 1 auto', overflow: 'auto', backgroundColor: '#e8ebef', p: 2 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        <Box ref={pagesRef} />
      </Box>
    </Drawer>
  );
};
